(() => {
  'use strict';
  if (customElements.get('cn-card-purchase')) return;
  const tx = source => window.cnUiTextLabels?.[source] ?? source;

  // A card cannot read blocks from the product's assigned template. Inspect that
  // real template before submitting a purchase; never infer model groups.
  const cache = new Map(), queue = [];
  let running = 0;
  function schedule(task, priority = false) {
    return new Promise((resolve, reject) => {
      const job = {task, resolve, reject};
      priority ? queue.unshift(job) : queue.push(job);
      drain();
    });
  }
  function drain() {
    while (running < 3 && queue.length) {
      const {task, resolve, reject} = queue.shift(); running++;
      Promise.resolve().then(task).then(resolve, reject).finally(() => { running--; drain(); });
    }
  }
  function productUrl(value, variant) {
    const url = new URL(value, location.origin);
    if (url.origin !== location.origin || !/^https?:$/.test(url.protocol) || !/\/products\/[^/]+\/?$/.test(url.pathname) || !/^\d+$/.test(variant)) throw new Error('Invalid product reference');
    url.searchParams.set('variant', variant); url.searchParams.delete('section_id'); url.hash = '';
    const current = new URL(location.href), theme = window.Shopify?.theme;
    const explicit = current.searchParams.get('preview_theme_id');
    const preview = /^\d+$/.test(explicit || '') ? explicit : (['unpublished', 'development'].includes(theme?.role) || window.Shopify?.designMode) && /^\d+$/.test(String(theme?.id || '')) ? String(theme.id) : null;
    if (preview) {
      url.searchParams.set('preview_theme_id', preview);
      for (const name of ['preview_key', 'key', '_fd', '_sc', '_ab']) if (current.searchParams.has(name)) url.searchParams.set(name, current.searchParams.get(name));
    }
    return url;
  }
  function inspect(html, url, variant) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const handle = decodeURIComponent(url.pathname.match(/\/products\/([^/]+)\/?$/)[1]);
    const product = [...doc.querySelectorAll('product-info[id^="MainProduct-"]')].find(node => {
      try { return (node.dataset.cnHandle || decodeURIComponent(new URL(node.dataset.url, url).pathname.match(/\/products\/([^/]+)\/?$/)?.[1] || '')) === handle; } catch { return false; }
    });
    if (!product) throw new Error('Product template unavailable');
    const form = [...product.querySelectorAll('product-form form[action*="/cart/add"]')].find(node => node.querySelector('input[name="id"]')?.value === variant);
    if (!form) throw new Error('Selected offer unavailable');
    const priceInput = form.querySelector('input[name="id"]');
    const priceReady = priceInput?.dataset.cnPriceReady ?? form.closest('product-form')?.dataset.cnPriceReady;
    const pricing = product.querySelector('[data-cn-purchase-pricing]');
    let pricePending = priceReady === 'false';
    if (pricing) {
      let selectedPrice;
      try { selectedPrice = JSON.parse(pricing.textContent); } catch { throw new Error('Offer price unavailable'); }
      if (String(selectedPrice.variantId) !== variant) throw new Error('Offer price does not match variant');
      if (!(Number(selectedPrice.price) > 0)) pricePending = true;
    }
    const formId = form.getAttribute('id');
    const controls = [...product.querySelectorAll('input,select,textarea')].filter(node => form.contains(node) || (formId && node.getAttribute('form') === formId));
    const disabled = node => node.disabled || node.closest('fieldset[disabled],optgroup[disabled]');
    const usable = node => !disabled(node) && !node.closest('[hidden],[inert]') && node.getAttribute('aria-disabled') !== 'true' && node.dataset.cnAvailable !== 'false' && !node.classList.contains('disabled');
    const pickers = [...product.querySelectorAll('variant-selects')].filter(usable);
    const variantControls = pickers.flatMap(picker => [...picker.querySelectorAll('input[type="radio"],select')]);
    const nativeChoice = variantControls.some(node => {
      if (!usable(node)) return false;
      if (node.tagName === 'SELECT') {
        const values = [...node.options].filter(option => option.value !== '');
        return new Set(values.map(option => option.value)).size > 1 && values.some(option => !option.selected && usable(option));
      }
      const group = variantControls.filter(other => other.type === 'radio' && other.name === node.name);
      return !node.checked && new Set(group.map(other => other.value)).size > 1;
    });
    const alternateOffer = [...product.querySelectorAll('.cn-sibling-configurator a.cn-configuration-offer[href], .cn-sibling-configurator a.cn-sibling-option[href], .cn-configurator-alternatives a[href]')].some(link => {
      if (!usable(link)) return false;
      try {
        const target = new URL(link.getAttribute('href'), url);
        return target.origin === url.origin && /\/products\/[^/]+\/?$/.test(target.pathname) && (target.pathname !== url.pathname || (target.searchParams.get('variant') && target.searchParams.get('variant') !== variant));
      } catch { return false; }
    });
    // A wrapper, one fixed option or a stale reference list is not a choice.
    if (nativeChoice || alternateOffer) return {kind:'configure'};
    if (pricePending) return {kind:'unavailable', pricePending:true};
    const quantity = controls.find(node => node.name === 'quantity');
    const button = form.querySelector('[type="submit"]');
    if (!button) throw new Error('Purchase form unavailable');
    if (button.disabled || button.getAttribute('aria-disabled') === 'true' || form.querySelector('input[name="id"]').disabled || (quantity && quantity.max !== '' && Number(quantity.max) < 1)) return {kind:'unavailable'};
    const editable = controls.filter(node => usable(node) && node.type !== 'hidden' && !node.readOnly && node.name !== 'quantity' && !node.closest('variant-selects'));
    if (editable.some(node => node.name === 'selling_plan' || node.required || node.name?.startsWith('properties['))) return {kind:'configure'};
    // Keep checkout constraints and app-owned data on the real product form.
    // Needing that form is not proof that a customer can configure the product.
    const copiedGift = node => node.name === 'properties[Gratis inklusive]' && node.type === 'hidden' && node.hasAttribute('data-cn-norton-gift-property');
    if (controls.some(node => !disabled(node) && (node.name === 'selling_plan' || (node.name?.startsWith('properties[') && !copiedGift(node)) || (node.required && node.name !== 'quantity' && !node.closest('variant-selects'))))) return {kind:'details'};
    if (quantity && ((Number(quantity.min) || 1) > 1 || (Number(quantity.step) || 1) > 1)) return {kind:'details'};
    const gift = form.querySelector('input[data-cn-norton-gift-property]');
    return {kind:'simple', hasVariantControls:variantControls.length > 0, gift:gift ? {name:gift.name, value:gift.value} : null};
  }
  function load(url, variant, fresh = false) {
    const key = url.href;
    const cached = cache.get(key);
    if (!fresh && cached && Date.now() - cached.time < 60000) return cached.promise;
    const promise = schedule(async () => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 12000);
      try {
        const response = await fetch(url.href, {credentials:'same-origin', signal:controller.signal, ...(fresh ? {cache:'no-store'} : {})});
        if (!response.ok) throw new Error('Product request unavailable');
        return inspect(await response.text(), url, variant);
      } finally { clearTimeout(timer); }
    }, fresh);
    const entry = {promise, time:Date.now()}; cache.set(key, entry);
    promise.catch(() => { if (cache.get(key) === entry) cache.delete(key); });
    return promise;
  }
  // Template-only groups are unavailable to card Liquid. Resolve visible cards
  // automatically, sharing requests and limiting concurrency to three pages.
  const observer = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
    for (const entry of entries) if (entry.isIntersecting) { observer.unobserve(entry.target); entry.target.probe(); }
  }) : null;
  class CardPurchase extends HTMLElement {
    connectedCallback() {
      if (this.initialized) { this.enableGuardedPurchase?.(); if (!this.dataset.purchaseState) observer?.observe(this); return; }
      this.initialize();
      if (!this.initialized) queueMicrotask(() => { if (this.isConnected) this.initialize(); });
    }
    disconnectedCallback() { observer?.unobserve(this); ++this.requestNumber; this.probing = false; }
    initialize() {
      this.native = this.querySelector('product-form');
      this.form = this.native?.querySelector('form');
      this.link = this.querySelector('[data-cn-card-configure]');
      if (!this.form || !this.link) return;
      this.button = this.form.querySelector('[type="submit"]');
      this.message = this.querySelector('[data-cn-card-purchase-message]');
      this.requestNumber = 0; this.initialized = true;
      this.addEventListener('submit', event => this.submit(event), true);
      try { const url = productUrl(this.dataset.productUrl, this.dataset.variantId); this.productPage = url.pathname + url.search; this.link.href = this.productPage + (this.dataset.initialConfiguration === 'true' ? '#cn-product-configuration' : ''); } catch { return; }
      // The server renders the purchase label immediately. Keep its form disabled
      // until both this capture guard and Shopify's native handler are available.
      const ready = this.enableGuardedPurchase = () => {
        if (this.isConnected && !this.dataset.purchaseState && typeof this.native.onSubmitHandler === 'function') this.button.disabled = this.dataset.initialAvailable !== 'true' || this.dataset.cnPriceReady === 'false';
      };
      if (customElements.get('product-form')) ready();
      else customElements.whenDefined('product-form').then(ready);
      observer?.observe(this);
      // Offscreen cards need no request. A click still performs a fresh check.
      this.addEventListener('pointerenter', () => this.probe());
      this.addEventListener('focusin', () => this.probe());
      this.addEventListener('focusout', () => queueMicrotask(() => {
        if (this.pendingResult && document.activeElement !== this.link && document.activeElement !== this.button) {
          const result = this.pendingResult; this.pendingResult = null; this.show(result);
        }
      }));
    }
    show(result, afterClick = false) {
      const direct = result.kind === 'simple' || result.kind === 'unavailable' || (result.kind === 'details' && !afterClick);
      this.link.hidden = direct; this.native.hidden = !direct;
      this.dataset.purchaseState = result.kind;
      this.button.removeAttribute('aria-disabled');
      this.button.disabled = result.kind === 'unavailable' || !direct;
      if (typeof result.pricePending === 'boolean' || result.kind === 'simple') {
        const ready = result.pricePending ? 'false' : 'true';
        this.dataset.cnPriceReady = ready; this.native.dataset.cnPriceReady = ready;
        const input = this.form.querySelector('[name="id"]'); input.dataset.cnPriceReady = ready; input.disabled = ready === 'false';
      }
      this.button.querySelector('[data-cn-card-add-label]').textContent = result.pricePending ? tx('Preis noch nicht verfügbar') : result.kind === 'unavailable' ? (window.variantStrings?.unavailable || tx('Nicht verfügbar')) : (window.variantStrings?.addToCart || tx('In den Warenkorb'));
      if (!direct) {
        this.link.firstChild.textContent = result.kind === 'configure' ? tx('Jetzt konfigurieren') : tx('Kauf auf Produktseite fortsetzen');
        this.link.href = this.productPage + (result.kind === 'configure' ? '#cn-product-configuration' : '');
      }
      this.form.querySelectorAll('[data-cn-norton-gift-property]').forEach(input => input.remove());
      if (result.gift?.name === 'properties[Gratis inklusive]') {
        const input = document.createElement('input'); input.type = 'hidden'; input.name = result.gift.name; input.value = result.gift.value; input.dataset.cnNortonGiftProperty = ''; this.form.append(input);
      }
    }
    verifiedResult(result) {
      // Metadata can prevent direct purchase, but cannot establish an actual UI choice.
      if (result.kind === 'simple' && (this.dataset.requiresSellingPlan === 'true' || (this.dataset.multipleVariants === 'true' && !result.hasVariantControls))) return {kind:'details'};
      return result;
    }
    async probe() {
      if (!this.isConnected || this.probing || this.dataset.purchaseState) return;
      this.probing = true;
      const request = ++this.requestNumber;
      try {
        const url = productUrl(this.dataset.productUrl, this.dataset.variantId);
        this.productPage = url.pathname + url.search;
        const result = this.verifiedResult(await load(url, this.dataset.variantId));
        if (!this.isConnected || request !== this.requestNumber) return;
        // Never enable the HTML form before its native guarded cart handler exists.
        if (!customElements.get('product-form') || typeof this.native.onSubmitHandler !== 'function') return;
        // Never remove the currently focused action or silently move focus.
        if (document.activeElement === this.link || document.activeElement === this.button) this.pendingResult = result;
        else this.show(result);
      } catch { /* Keep the guarded purchase action; a failed probe never adds. */ }
      finally { if (request === this.requestNumber) this.probing = false; }
    }
    async submit(event) {
      event.preventDefault(); event.stopImmediatePropagation();
      if (this.busy || this.button.disabled || this.button.getAttribute('aria-disabled') === 'true' || this.native.hidden || this.dataset.cnPriceReady === 'false') return;
      this.busy = true; ++this.requestNumber; this.pendingResult = null; this.button.setAttribute('aria-disabled', 'true'); this.button.setAttribute('aria-busy', 'true');
      this.button.classList.add('loading'); this.button.querySelector('.loading__spinner')?.classList.remove('hidden');
      this.message.hidden = true;
      let handedToNative = false;
      try {
        const url = productUrl(this.dataset.productUrl, this.dataset.variantId);
        const result = this.verifiedResult(await load(url, this.dataset.variantId, true));
        if (!this.isConnected) return;
        this.show(result, true);
        if (result.kind !== 'simple') {
          this.message.textContent = result.kind === 'configure' ? tx('Bitte wähle die passende Ausführung auf der Produktseite.') : result.kind === 'unavailable' ? tx('Dieses Angebot ist aktuell nicht verfügbar.') : tx('Bitte prüfe dieses Angebot auf der Produktseite. Es wurde nichts hinzugefügt.');
          this.message.hidden = false;
          if (result.kind !== 'unavailable') this.link.focus();
          else { this.message.tabIndex = -1; this.message.focus(); }
          return;
        }
        this.form.querySelector('[name="quantity"]').value = '1';
        this.native.onSubmitHandler(event);
        handedToNative = true;
      } catch {
        if (!this.isConnected) return;
        this.show({kind:'details'}, true);
        this.message.textContent = tx('Bitte prüfe dieses Angebot auf der Produktseite. Es wurde nichts hinzugefügt.');
        this.message.hidden = false; this.link.focus();
      } finally {
        this.busy = false; this.button.removeAttribute('aria-busy');
        if (!handedToNative) { this.button.classList.remove('loading'); this.button.querySelector('.loading__spinner')?.classList.add('hidden'); }
      }
    }
  }
  customElements.define('cn-card-purchase', CardPurchase);
})();
